// pages/LoginPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "../components/AppShell";

import {
  login as loginApi,
  login2fa,
  getCurrentUser,
  fetchGlobalFeatureFlags,
  setCachedFeatureFlags,
  consumeAuthExpiredNotice,
} from "../services/api";

export default function LoginPage({ onLoggedIn }) {
  const navigate = useNavigate();

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState(null);
  const [loginBusy, setLoginBusy] = useState(false);

  // 2FA state
  const [needs2fa, setNeeds2fa] = useState(false);
  const [pendingToken, setPendingToken] = useState("");
  const [tfaCode, setTfaCode] = useState("");

  const [notice, setNotice] = useState(null);

  // show one-time "session expired" message if present
  useEffect(() => {
    const msg = consumeAuthExpiredNotice();
    if (msg) setNotice(msg);
  }, []);

  const completeLogin = async () => {
    const me = await getCurrentUser();
    try {
      const flags = await fetchGlobalFeatureFlags();
      setCachedFeatureFlags(flags);
    } catch {
      setCachedFeatureFlags({ copilotDisabled: false });
    }
    onLoggedIn?.(me);
    setLoginPassword("");
    navigate("/modules", { replace: true });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      setLoginError("Email and password are required.");
      return;
    }

    setLoginBusy(true);
    setLoginError(null);

    try {
      const data = await loginApi(loginEmail.trim(), loginPassword);

      if (data?.requires_2fa) {
        setPendingToken(data.pending_token);
        setNeeds2fa(true);
        setLoginBusy(false);
        return;
      }

      await completeLogin();
    } catch (err) {
      setLoginError(err?.message || "Login failed.");
    } finally {
      setLoginBusy(false);
    }
  };

  const handle2fa = async (e) => {
    e.preventDefault();
    if (!tfaCode.trim()) {
      setLoginError("Enter the 6-digit code from your authenticator app.");
      return;
    }

    setLoginBusy(true);
    setLoginError(null);

    try {
      await login2fa(pendingToken, tfaCode.trim());
      await completeLogin();
    } catch (err) {
      setLoginError(err?.message || "Invalid code. Try again.");
    } finally {
      setLoginBusy(false);
    }
  };

  return (
    <AppShell headerSubtitle="MAD Discovery — Login">
      <header className="page-header">
        <div>
          <h1 className="page-title">MAD Discovery — Login</h1>
          <p className="page-sub">Sign in to access modules.</p>
        </div>
      </header>

      <div
        style={{
          marginTop: 20,
          padding: "14px 20px",
          borderRadius: 10,
          background: "linear-gradient(120deg, #000000 0%, #ffcc33 100%)",
          color: "white",
          textAlign: "center",
          fontWeight: 700,
          fontSize: 15,
          letterSpacing: 0.4,
          boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
        }}
      >
        MAD ABOUT DIGITAL
      </div>

      <main>
        <div
          style={{
            maxWidth: 420,
            margin: "40px auto 0",
            padding: 24,
            borderRadius: 12,
            border: "1px solid var(--border)",
            background: "#fff",
            boxShadow: "0 12px 32px rgba(15,23,42,0.16)",
          }}
        >
          {notice ? (
            <div
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid rgba(245,158,11,0.6)",
                background: "#fffbeb",
                color: "#92400e",
                fontSize: 13,
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              {notice}
            </div>
          ) : null}

          {!needs2fa ? (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 14 }}>
                <label
                  htmlFor="email"
                  style={{ display: "block", marginBottom: 6, fontWeight: 700 }}
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="fld"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="admin@example.com"
                  disabled={loginBusy}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label
                  htmlFor="password"
                  style={{ display: "block", marginBottom: 6, fontWeight: 700 }}
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  className="fld"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Your password"
                  disabled={loginBusy}
                />
              </div>

              {loginError ? (
                <div style={{ marginBottom: 12, fontSize: 13, color: "#b91c1c" }}>
                  {loginError}
                </div>
              ) : null}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate("/forgot-password")}
                  onKeyDown={(e) => { if (e.key === "Enter") navigate("/forgot-password"); }}
                  style={{ color: "#2563eb", fontSize: 13, cursor: "pointer", textDecoration: "none" }}
                >
                  Forgot password?
                </span>
                <button type="submit" className="btn-primary" disabled={loginBusy}>
                  {loginBusy ? "Signing in…" : "Login"}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handle2fa}>
              <div style={{ marginBottom: 12, fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
                Two-factor authentication is enabled for this account.
                Enter the 6-digit code from your authenticator app, or use a recovery code.
              </div>

              <div style={{ marginBottom: 14 }}>
                <label
                  htmlFor="tfa-code"
                  style={{ display: "block", marginBottom: 6, fontWeight: 700 }}
                >
                  Authentication Code
                </label>
                <input
                  id="tfa-code"
                  type="text"
                  className="fld"
                  value={tfaCode}
                  onChange={(e) => setTfaCode(e.target.value)}
                  placeholder="123456 or ABCD-1234"
                  disabled={loginBusy}
                  autoFocus
                  autoComplete="one-time-code"
                  style={{ letterSpacing: 2, fontSize: 18, textAlign: "center" }}
                />
              </div>

              {loginError ? (
                <div style={{ marginBottom: 12, fontSize: 13, color: "#b91c1c" }}>
                  {loginError}
                </div>
              ) : null}

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setNeeds2fa(false); setPendingToken(""); setTfaCode(""); setLoginError(null); }}
                  disabled={loginBusy}
                >
                  Back
                </button>
                <button type="submit" className="btn-primary" disabled={loginBusy || !tfaCode.trim()}>
                  {loginBusy ? "Verifying…" : "Verify"}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
    </AppShell>
  );
}
